import React, { useState, useEffect } from 'react';
import { customersAPI, settingsAPI } from '../services/api';
import { formatDateInIsrael } from '../utils/dateUtils';
import { searchBooks } from '../utils/searchHelpers';
import ReturnBookModal from './ReturnBookModal';
import ActivityHistoryTab from './ActivityHistoryTab';

function CustomerModal({ customerIdentityId, onClose, onUpdate }) {
  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loans, setLoans] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activityHistory, setActivityHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Return book modal state
  const [showReturnBookModal, setShowReturnBookModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  
  // Books selection state for bulk return
  const [selectedBooksForReturn, setSelectedBooksForReturn] = useState({});
  const [showReturnBooksForm, setShowReturnBooksForm] = useState(false);

  // Extend loan state
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendingLoan, setExtendingLoan] = useState(null);
  const [newReturnDate, setNewReturnDate] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  // Helper to calculate dates - exactly 1 calendar month
  const getDefaultDates = () => {
    const today = new Date();
    
    // בדוק אם יש מנוי פעיל כרגע
    const hasActiveSubscription = customer?.subscription_start_date && customer?.subscription_end_date && 
                                   new Date(customer.subscription_start_date) <= today && 
                                   new Date(customer.subscription_end_date) >= today;
    
    let startDate;
    let endDate;
    
    if (hasActiveSubscription) {
      // אם יש מנוי פעיל, התחל מתאריך סיום המנוי הנוכחי
      startDate = new Date(customer.subscription_end_date);
      endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate());
    } else {
      // אחרת, התחל מהיום
      startDate = today;
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
    }
    
    // טפל בגלישת ימים בחודשים שונים
    const lastDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
    if (endDate.getDate() !== startDate.getDate()) {
      endDate.setDate(lastDayOfMonth);
    }
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  const [subscriptionData, setSubscriptionData] = useState({
    subscription_start_date: '',
    subscription_end_date: '',
    books_in_subscription: 1,
    paymentAmount: '5'
  });

  const [settings, setSettings] = useState(null);

  const [newNote, setNewNote] = useState('');
  const [showAddNote, setShowAddNote] = useState(false);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  
  // Fine payment state
  const [showPayFine, setShowPayFine] = useState(false);
  const [fineAmount, setFineAmount] = useState('');

  const [bookSearch, setBookSearch] = useState('');
  const [bookSearchResults, setBookSearchResults] = useState([]);
  const [showBookSearchResults, setShowBookSearchResults] = useState(false);

  // Load customer data
  useEffect(() => {
    loadCustomerData();
  }, [customerIdentityId]);

  // Load system settings to use as defaults
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await settingsAPI.getAll();
        console.log('Settings loaded in CustomerDetailModal:', res.data);
        // Extract just the values from the nested object
        const settingsValues = {};
        Object.entries(res.data || {}).forEach(([key, setting]) => {
          settingsValues[key] = typeof setting === 'object' ? setting.value : setting;
        });
        if (mounted) setSettings(settingsValues);
      } catch (err) {
        console.error('Error loading settings in CustomerDetailModal:', err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching customer data for ID:', customerIdentityId);
      const response = await customersAPI.getById(customerIdentityId);
      const data = response.data;
      
      console.log('Loaded customer data:', data.customer);
      console.log('📊 Loans count:', data.loans?.length || 0);
      console.log('📋 Loans:', data.loans);
      
      setCustomer(data.customer);
      setNotes(data.notes || []);
      setLoans(data.loans || []);
      setPaymentHistory(data.paymentHistory || []);
      setActivityHistory(data.activityHistory || []);

      setFormData({
        name: data.customer.name || '',
        phone: data.customer.phone || '',
        email: data.customer.email || '',
        address: data.customer.address || '',
        notes: data.customer.notes || ''
      });

      // Convert ISO dates to YYYY-MM-DD format for input type="date"
      const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
      };

      const startDate = formatDateForInput(data.customer.subscription_start_date);
      const endDate = formatDateForInput(data.customer.subscription_end_date);
      
      console.log('Subscription dates - Start:', startDate, 'End:', endDate);

      setSubscriptionData({
        subscription_start_date: startDate,
        subscription_end_date: endDate,
        books_in_subscription: data.customer.books_in_subscription || 1,
        paymentAmount: (settings && settings.subscription_price 
          ? (parseFloat(settings.subscription_price) * (data.customer.books_in_subscription || 1)).toString()
          : (5 * (data.customer.books_in_subscription || 1)).toString())
      });

      setError('');
    } catch (err) {
      console.error('Error loading customer:', err);
      setError('שגיאה בטעינת נתוני הלקוח');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await customersAPI.update(customerIdentityId, formData);
      setError('');
      loadCustomerData();
      alert('הלקוח עודכן בהצלחה');
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('שגיאה בעדכון הלקוח');
    } finally {
      setLoading(false);
    }
  };

  // Toggle subscription form; when opening, prefill defaults from settings
  const toggleSubscriptionForm = () => {
    if (!showSubscriptionForm) {
      const defaults = getDefaultDates();
      const currentBooks = subscriptionData.books_in_subscription || 1;
      const pricePerBook = settings && settings.subscription_price 
        ? parseFloat(settings.subscription_price)
        : 5;
      const defaultPayment = (pricePerBook * currentBooks).toString();

      // תמיד אתחל עם התאריכים החדשים
      setSubscriptionData(prev => ({
        ...prev,
        subscription_start_date: defaults.start,
        subscription_end_date: defaults.end,
        books_in_subscription: currentBooks,
        paymentAmount: defaultPayment
      }));
    }
    setShowSubscriptionForm(prev => !prev);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      setLoading(true);
      await customersAPI.addNote(customerIdentityId, newNote);
      setNewNote('');
      setShowAddNote(false);
      loadCustomerData();
    } catch (err) {
      console.error('Error adding note:', err);
      alert('שגיאה בהוספת הערה');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // If dates are empty, use defaults
      let startDate = subscriptionData.subscription_start_date;
      let endDate = subscriptionData.subscription_end_date;
      
      if (!startDate || !endDate) {
        const dates = getDefaultDates();
        startDate = startDate || dates.start;
        endDate = endDate || dates.end;
      }
      
      // 1. Update subscription details
      await customersAPI.updateSubscription(customerIdentityId, {
        subscription_start_date: startDate,
        subscription_end_date: endDate,
        books_in_subscription: subscriptionData.books_in_subscription
      });

      // 2. Add payment if amount is provided
      if (subscriptionData.paymentAmount && parseFloat(subscriptionData.paymentAmount) > 0) {
        await customersAPI.addPayment(customerIdentityId, {
          payment_type: 'subscription',
          amount: parseFloat(subscriptionData.paymentAmount),
          description: 'תשלום מנוי'
        });
      }

      setShowSubscriptionForm(false);
      loadCustomerData();
      alert('המנוי עודכן בהצלחה');
    } catch (err) {
      console.error('Error updating subscription:', err);
      alert('שגיאה בעדכון המנוי');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFine = async (e) => {
    e.preventDefault();
    if (!fineAmount || parseFloat(fineAmount) <= 0) {
      alert('סכום התשלום חייב להיות גדול מ-0');
      return;
    }
    try {
      setLoading(true);
      await customersAPI.addPayment(customerIdentityId, {
        payment_type: 'fine',
        amount: parseFloat(fineAmount),
        description: 'תשלום קנס'
      });
      setFineAmount('');
      setShowPayFine(false);
      loadCustomerData();
      alert('תשלום קנס נקלט בהצלחה');
    } catch (err) {
      console.error('Error paying fine:', err);
      alert('שגיאה בתשלום הקנס');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchBook = async (searchTerm) => {
    setBookSearch(searchTerm);
    if (searchTerm.trim().length < 1) {
      setBookSearchResults([]);
      setShowBookSearchResults(false);
      return;
    }
    const results = await searchBooks(searchTerm, true);
    setBookSearchResults(results);
    setShowBookSearchResults(true);
  };

  const handleSelectBook = async (book) => {
    try {
      // בדוק שיש ספר בחר
      if (!book || !book.id) {
        alert('❌ לא נבחר ספר. אנא בחר ספר מהרשימה.');
        return;
      }

      // בדוק אם יש טלפון חסר בלבד
      if (!customer?.phone) {
        alert('⚠️ לא ניתן להשאיל ספר ללקוח ללא מספר טלפון.\n\nאנא עדכן את מספר הטלפון.');
        return;
      }

      setLoading(true);
      const bookTitle = book.book_name || book.title || 'ספר לא ידוע';
      console.log('🔄 Borrowing book for customer:', bookTitle);
      // ה-backend יחשב את return_date בעצמו בהתאם לis_new של הספר
      await customersAPI.borrowBook(customerIdentityId, {
        book_id: book.id,
        book_condition: book.condition || 'good'
      });
      console.log('✅ Book borrowed successfully:', bookTitle);
      setBookSearch('');
      setBookSearchResults([]);
      setShowBookSearchResults(false);
      loadCustomerData();
    } catch (error) {
      console.error('Error borrowing book:', error);
      if (error.response?.status === 400) {
        alert(error.response.data?.message || 'שגיאה בהשאלת הספר - בדוק את תנאי המנוי');
      } else {
        alert('שגיאה בהשאלת הספר: ' + (error.message || 'שגיאה לא ידועה'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReturnBook = (loan) => {
    setSelectedBook(loan);
    setShowReturnBookModal(true);
  };

  const handleReturnBookSuccess = () => {
    console.log('🔄 handleReturnBookSuccess called');
    console.log('📥 Calling loadCustomerData()...');
    loadCustomerData();
    //onClose && onClose();
  };

  const handleExtendLoan = (loan) => {
    setExtendingLoan(loan);
    console.log('🔍 handleExtendLoan loan object:', loan);
    console.log('📅 return_date value:', loan.return_date);
    
    // Set default: 7 days for new books, 14 days for regular books
    const extensionDays = loan.is_new ? 7 : 14;
    console.log('📅 Extension period:', extensionDays, 'days (is_new:', loan.is_new, ')');
    let baseDate = null;
    
    try {
      if (loan.return_date) {
        // Use return_date if available - it's already in ISO format
        baseDate = new Date(loan.return_date);
      } else if (loan.loan_date) {
        // If return_date is null, use loan_date as base - it's also in ISO format
        console.log('⚠️ return_date is null, using loan_date as base:', loan.loan_date);
        baseDate = new Date(loan.loan_date);
      } else {
        // Last resort: use today
        console.log('⚠️ Both return_date and loan_date are null, using today');
        baseDate = new Date();
      }
      
      if (!baseDate || isNaN(baseDate.getTime())) {
        console.error('❌ Invalid date parsed:', { return_date: loan.return_date, loan_date: loan.loan_date });
        baseDate = new Date();
      }
      
      const defaultNewDate = new Date(baseDate);
      defaultNewDate.setDate(defaultNewDate.getDate() + extensionDays);
      
      const formattedDate = defaultNewDate.toISOString().split('T')[0];
      console.log('📅 Extend loan - Base date:', baseDate.toISOString(), 'New default:', formattedDate);
      setNewReturnDate(formattedDate);
      setShowExtendDialog(true);
    } catch (error) {
      console.error('❌ Error in handleExtendLoan:', error);
      alert('שגיאה בפתיחת חלונית ההארכה');
    }
  };

  const handleConfirmExtend = async () => {
    if (!extendingLoan || !newReturnDate) return;
    try {
      setLoading(true);
      console.log('🔄 Extending loan:', { loanId: extendingLoan.id, newReturnDate });
      await customersAPI.extendLoan(customerIdentityId, extendingLoan.id, {
        loan_id: extendingLoan.id,
        new_return_date: newReturnDate
      });
      setShowExtendDialog(false);
      setExtendingLoan(null);
      setNewReturnDate('');
      console.log('✅ Loan extended, refreshing data...');
      await loadCustomerData();
      onUpdate && onUpdate();
      alert('הלוואה הורחקה בהצלחה');
    } catch (error) {
      console.error('Error extending loan:', error);
      alert('שגיאה בהארכת ההלוואה');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelExtend = () => {
    setShowExtendDialog(false);
    setExtendingLoan(null);
    setNewReturnDate('');
  };

  // Toggle checkbox for book selection
  const handleToggleBooksSelection = (loanId) => {
    setSelectedBooksForReturn(prev => ({
      ...prev,
      [loanId]: !prev[loanId]
    }));
  };

  // Return all books at once
  const handleReturnAllBooks = async () => {
    if (borrowedLoans.length === 0) {
      alert('אין ספרים להחזרה');
      return;
    }

    if (window.confirm(`האם אתה בטוח שברצונך להחזיר את כל ${borrowedLoans.length} הספרים?`)) {
      try {
        setLoading(true);
        // Return each book
        for (const loan of borrowedLoans) {
          await customersAPI.returnBook(customerIdentityId, {
            book_id: loan.current_book_id || loan.book_id,
            book_condition: 'טוב',
            return_notes: 'החזרה ישירה'
          });
        }
        alert(`${borrowedLoans.length} ספרים הוחזרו בהצלחה`);
        setSelectedBooksForReturn({});
        loadCustomerData();
        onUpdate && onUpdate();
      } catch (error) {
        console.error('Error returning books:', error);
        alert('שגיאה בהחזרת ספרים');
      } finally {
        setLoading(false);
      }
    }
  };

  // Return selected books
  const handleReturnSelectedBooks = async () => {
    const selectedLoans = borrowedLoans.filter(loan => selectedBooksForReturn[loan.id]);
    
    if (selectedLoans.length === 0) {
      alert('אנא בחר ספרים להחזרה');
      return;
    }

    if (window.confirm(`האם אתה בטוח שברצונך להחזיר ${selectedLoans.length} ספרים?`)) {
      try {
        setLoading(true);
        // Return each selected book
        for (const loan of selectedLoans) {
          await customersAPI.returnBook(customerIdentityId, {
            book_id: loan.current_book_id || loan.book_id,
            book_condition: 'טוב',
            return_notes: 'החזרה ישירה'
          });
        }
        alert(`${selectedLoans.length} ספרים הוחזרו בהצלחה`);
        setSelectedBooksForReturn({});
        setShowReturnBooksForm(false);
        loadCustomerData();
        onUpdate && onUpdate();
      } catch (error) {
        console.error('Error returning books:', error);
        alert('שגיאה בהחזרת ספרים');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading && !customer) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '20px' }}>טוען...</div>
        </div>
      </div>
    );
  }

  if (error && !customer) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>שגיאה</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div style={{ padding: '20px', color: 'red' }}>{error}</div>
        </div>
      </div>
    );
  }

  const borrowedLoans = loans.filter(loan => loan.status === 'borrowed');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{customer?.name} (תעודת זהות: {customer?.identity_id})</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div style={{ padding: '10px', backgroundColor: '#fee', color: '#a00', borderRadius: '3px', marginBottom: '10px' }}>{error}</div>}

        {/* Missing Details Warning */}
        {(!customer?.phone || !customer?.address) && (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            borderRadius: '5px', 
            marginBottom: '15px',
            border: '1px solid #ffc107'
          }}>
            <strong>⚠️ חסרים פרטים חובה:</strong>
            {!customer?.phone && <div>• טלפון לא מעודכן</div>}
            <div style={{ marginTop: '8px', fontSize: '12px' }}>
              אנא עדכן את הטלפון כדי שניתן יהיה להשאיל ספרים ללקוח זה.
            </div>
          </div>
        )}

        {/* Customer Details */}
        <form onSubmit={handleSaveCustomer}>
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
            <h3>פרטי הלקוח</h3>
            <div className="form-group">
              <label>שם</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>טלפון</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>מייל</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>כתובת</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={loading}
                rows="2"
              />
            </div>
            <div className="form-group">
              <label>הערות</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={loading}
                rows="2"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ marginRight: '10px' }}>
              שמור שינויים
            </button>
          </div>
        </form>

        {/* Subscription Info */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f7f0', borderRadius: '5px' }}>
          <h3>מנוי</h3>
          <div style={{ marginBottom: '10px' }}>
            {customer?.subscription_start_date && customer?.subscription_end_date ? (
              <>
                <p><strong>תוקף מנוי:</strong> {formatDateInIsrael(customer.subscription_start_date)} עד {formatDateInIsrael(customer.subscription_end_date)}</p>
                <p><strong>סטטוס:</strong> 
                  <span style={{ marginRight: '5px', padding: '3px 8px', backgroundColor: (new Date(customer.subscription_start_date) <= new Date() && new Date(customer.subscription_end_date) >= new Date()) ? '#28a745' : '#dc3545', color: 'white', borderRadius: '3px' }}>
                    {(new Date(customer.subscription_start_date) <= new Date() && new Date(customer.subscription_end_date) >= new Date()) ? '✓ פעיל' : '❌ לא פעיל'}
                  </span>
                </p>
              </>
            ) : (
              <p><strong>תוקף מנוי:</strong> אין מנוי פעיל</p>
            )}
            <p><strong>מספר ספרים במנוי:</strong> {customer?.books_in_subscription || 1}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <p style={{ margin: 0 }}><strong>קנסות לא משולמות:</strong> {customer?.outstanding_fines || 0} ₪</p>
              <button 
                type="button" 
                className="btn btn-sm btn-warning"
                onClick={() => setShowPayFine(!showPayFine)}
              >
                {showPayFine ? 'ביטול' : 'שלם קנס'}
              </button>
            </div>
          </div>

          {showPayFine && (
            <form onSubmit={handlePayFine} style={{ marginTop: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '3px', border: '1px solid #ddd' }}>
              <div className="form-group">
                <label>סכום לתשלום (₪)</label>
                <input
                  type="number"
                  value={fineAmount}
                  onChange={(e) => setFineAmount(e.target.value)}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  disabled={loading}
                />
              </div>
              <button type="submit" className="btn btn-success btn-sm" disabled={loading}>בצע תשלום</button>
            </form>
          )}

          <button
            type="button"
            className="btn btn-primary"
            onClick={toggleSubscriptionForm}
            style={{ marginBottom: '10px', marginTop: '10px' }}
          >
            {showSubscriptionForm ? 'ביטול' : 'עדכן מנוי'}
          </button>

          {showSubscriptionForm && (
            <form onSubmit={handleUpdateSubscription} style={{ marginTop: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '3px' }}>
              <div className="form-group">
                <label>תאריך התחלה</label>
                <input
                  type="date"
                  value={subscriptionData.subscription_start_date || getDefaultDates().start}
                  onChange={(e) => setSubscriptionData(prev => ({ ...prev, subscription_start_date: e.target.value }))}
                  onFocus={(e) => {
                    if (!subscriptionData.subscription_start_date) {
                      const dates = getDefaultDates();
                      setSubscriptionData(prev => ({
                        ...prev,
                        subscription_start_date: dates.start,
                        subscription_end_date: dates.end
                      }));
                    }
                  }}
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>תאריך סיום</label>
                <input
                  type="date"
                  value={subscriptionData.subscription_end_date || getDefaultDates().end}
                  onChange={(e) => setSubscriptionData(prev => ({ ...prev, subscription_end_date: e.target.value }))}
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>מספר ספרים</label>
                <input
                  type="number"
                  value={subscriptionData.books_in_subscription}
                  onChange={(e) => {
                    const numBooks = Math.max(1, parseInt(e.target.value) || 1);
                    const pricePerBook = settings && settings.subscription_price 
                      ? parseFloat(settings.subscription_price)
                      : 5;
                    console.log('Books changed:', { numBooks, pricePerBook, settings: settings?.subscription_price });
                    setSubscriptionData(prev => ({
                      ...prev,
                      books_in_subscription: numBooks,
                      paymentAmount: (pricePerBook * numBooks).toString()
                    }));
                  }}
                  min="1"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>סכום לתשלום (אופציונלי)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const numBooks = Math.max(1, subscriptionData.books_in_subscription - 1);
                      const pricePerBook = settings && settings.subscription_price 
                        ? parseFloat(settings.subscription_price)
                        : 5;
                      setSubscriptionData(prev => ({
                        ...prev,
                        books_in_subscription: numBooks,
                        paymentAmount: (pricePerBook * numBooks).toString()
                      }));
                    }}
                    disabled={loading}
                    style={{ padding: '5px 10px' }}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={subscriptionData.paymentAmount}
                    onChange={(e) => setSubscriptionData(prev => ({ ...prev, paymentAmount: e.target.value }))}
                    min="0"
                    step="1"
                    placeholder="5.00"
                    disabled={loading}
                    style={{ flex: 1, textAlign: 'center' }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const numBooks = subscriptionData.books_in_subscription + 1;
                      const pricePerBook = settings && settings.subscription_price 
                        ? parseFloat(settings.subscription_price)
                        : 5;
                      setSubscriptionData(prev => ({
                        ...prev,
                        books_in_subscription: numBooks,
                        paymentAmount: (pricePerBook * numBooks).toString()
                      }));
                    }}
                    disabled={loading}
                    style={{ padding: '5px 10px' }}
                  >
                    +
                  </button>
                  <span style={{ marginLeft: '5px', color: '#666', fontSize: '14px' }}>₪</span>
                </div>
              </div>
              <button type="submit" className="btn btn-success" disabled={loading}>שמור מנוי</button>
            </form>
          )}
        </div>

        {/* Borrow/Return Books */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
          <h3>השאל ספר</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <input
              type="text"
              placeholder="חפש ספר"
              value={bookSearch}
              onChange={(e) => handleSearchBook(e.target.value)}
              onFocus={() => bookSearch && setShowBookSearchResults(true)}
              style={{ flex: 1 }}
              disabled={loading}
            />
          </div>

          {showBookSearchResults && bookSearchResults.length > 0 && (
            <div style={{ border: '1px solid #ddd', borderRadius: '3px', maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
              {bookSearchResults.map((book) => (
                <div
                  key={book.id}
                  style={{
                    padding: '10px',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                  }}
                  onClick={() => handleSelectBook(book)}
                >
                  <div><strong>{book.book_name}</strong></div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>{book.author} - {book.barcode}</div>
                </div>
              ))}
            </div>
          )}

          {borrowedLoans.length > 0 && (
            <>
              <h4>ספרים בהשאלה ({borrowedLoans.length})</h4>
              
              {/* Buttons for bulk return */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleReturnAllBooks}
                  disabled={loading}
                  title="החזר את כל הספרים בבת אחת"
                >
                  🔄 החזר הכל ({borrowedLoans.length})
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowReturnBooksForm(!showReturnBooksForm)}
                  disabled={loading}
                >
                  {showReturnBooksForm ? '✓ סגור בחירה' : '📋 בחר ספרים'}
                </button>
              </div>

              {/* Selection mode */}
              {showReturnBooksForm && (
                <div style={{ padding: '10px', backgroundColor: '#fff9e6', borderRadius: '5px', marginBottom: '10px', border: '2px solid #ffc107' }}>
                  <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                    בחר ספרים להחזרה ({Object.values(selectedBooksForReturn).filter(Boolean).length} נבחרו):
                  </div>
                  {borrowedLoans.map((loan) => (
                    <div key={loan.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', backgroundColor: 'white', marginBottom: '6px', borderRadius: '3px' }}>
                      <input
                        type="checkbox"
                        id={`book-${loan.id}`}
                        checked={selectedBooksForReturn[loan.id] || false}
                        onChange={() => handleToggleBooksSelection(loan.id)}
                        disabled={loading}
                        style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                      />
                      <label htmlFor={`book-${loan.id}`} style={{ flex: 1, cursor: 'pointer', margin: 0 }}>
                        {loan.book_title} ({loan.barcode}) - {formatDateInIsrael(loan.loan_date)}
                      </label>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-warning btn-sm"
                    onClick={handleReturnSelectedBooks}
                    disabled={loading || Object.values(selectedBooksForReturn).every(v => !v)}
                    style={{ marginTop: '10px', width: '100%' }}
                  >
                    ✓ החזר {Object.values(selectedBooksForReturn).filter(Boolean).length} ספרים
                  </button>
                </div>
              )}

              {/* List mode */}
              {!showReturnBooksForm && (
                <>
                  {borrowedLoans.map((loan) => (
                    <div key={loan.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: 'white', marginBottom: '8px', borderRadius: '3px' }}>
                      <span>{loan.book_title} ({loan.barcode}) - {formatDateInIsrael(loan.loan_date)}</span>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          type="button"
                          className="btn btn-warning btn-sm"
                          onClick={() => handleReturnBook(loan)}
                          disabled={loading}
                        >
                          💾 החזר
                        </button>
                        <button
                          type="button"
                          className="btn btn-info btn-sm"
                          onClick={() => handleExtendLoan(loan)}
                          disabled={loading}
                          title="הארך את תאריך ההחזרה"
                        >
                          ⏱️ הארך
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {/* Extend Loan Dialog */}
        {showExtendDialog && extendingLoan && (
          <div className="modal-overlay" style={{ position: 'fixed', zIndex: 1001 }}>
            <div className="modal-content" style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h3>הארכת הלוואה</h3>
                <button className="close-btn" onClick={handleCancelExtend}>×</button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4>פרטי ההלוואה</h4>
                {(() => {
                  console.log('🔍 extendingLoan data:', extendingLoan);
                  console.log('📅 return_date value:', extendingLoan?.return_date);
                  return null;
                })()}
                <p><strong>ספר:</strong> {extendingLoan.book_title}</p>
                <p><strong>ברקוד:</strong> {extendingLoan.barcode}</p>
                <p><strong>תאריך השאלה:</strong> {formatDateInIsrael(extendingLoan.loan_date)}</p>
                <p>
                  <strong>תאריך החזרה הנוכחי:</strong>{' '}
                  {extendingLoan.return_date 
                    ? formatDateInIsrael(extendingLoan.return_date)
                    : extendingLoan.loan_date 
                    ? `${formatDateInIsrael(extendingLoan.loan_date)} (מבוסס על תאריך השאלה)`
                    : 'לא קבוע'
                  }
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4>תאריך החזרה חדש</h4>
                <input
                  type="date"
                  value={newReturnDate}
                  onChange={(e) => setNewReturnDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn btn-success" 
                  onClick={handleConfirmExtend}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ✓ הארך הלוואה
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={handleCancelExtend}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment History */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3>היסטוריית תשלומים</h3>
          </div>

          {paymentHistory.length > 0 ? (
            <div style={{ overflowX: 'auto', maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee' }}>
              <table style={{ width: '100%', fontSize: '0.9rem', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f9f9f9' }}>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ textAlign: 'right', padding: '8px' }}>תאריך</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>סוג</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>סכום</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>תיאור</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment) => (
                    <tr key={payment.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>{formatDateInIsrael(payment.payment_date)}</td>
                      <td style={{ padding: '8px' }}>{payment.payment_type}</td>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>{payment.amount} ₪</td>
                      <td style={{ padding: '8px', color: '#666' }}>{payment.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#999' }}>אין היסטוריית תשלומים</p>
          )}
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3>הערות</h3>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowAddNote(!showAddNote)}
              disabled={loading}
            >
              {showAddNote ? 'ביטול' : 'הוסף הערה'}
            </button>
          </div>

          {showAddNote && (
            <div style={{ marginBottom: '10px' }}>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="הוסף הערה..."
                style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
                rows="2"
                disabled={loading}
              />
              <button type="button" className="btn btn-success" onClick={handleAddNote} disabled={loading}>שמור הערה</button>
            </div>
          )}

          {notes.length > 0 ? (
            <div>
              {notes.map((note) => (
                <div key={note.id} style={{ padding: '10px', backgroundColor: '#f0f8ff', borderRight: '3px solid #667eea', marginBottom: '8px', borderRadius: '3px' }}>
                  <p style={{ margin: '5px 0' }}>{note.note}</p>
                  <small style={{ color: '#999' }}>{formatDateInIsrael(note.created_at)}</small>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#999' }}>אין הערות</p>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button type="button" className="btn btn-primary" onClick={onClose}>סגור</button>
        </div>

        {/* Return Book Modal */}
        {showReturnBookModal && selectedBook && (
          <ReturnBookModal
            book={selectedBook}
            customer={customer}
            onClose={() => {
              setShowReturnBookModal(false);
              setSelectedBook(null);
            }}
            onSuccess={handleReturnBookSuccess}
          />
        )}
      </div>
    </div>
  );
}

export default CustomerModal;
